/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import React, { useCallback, useState } from 'react';
import { Styled as S } from '../../VerticalSolver.styled';
import { AssistElement } from '../../../types/model';
import { MathMessageParameters } from '../../../types/types1';
import { Wikitext } from '../wiki/Wikitext';
import { FlexRow } from '../../../types/Flex';

function buildLine(str: string, messageParameters?: MathMessageParameters): string {
    if (messageParameters) {
        return messageParameters.reduce((p, c) => {
            if (c.type === 'text') {
                return p.replace(`$${c.key}`, JSON.stringify(c.value));
            }

            if (c.type === 'grl') {
                return p.replace(`$${c.key}`, `<grl>${c.value}</grl>`);
            }

            return p;
        }, str);
    }
    return str;
}

interface PopoverProps {
    assist: AssistElement;
    onMessageChange?: () => void;
}

export const Popover = React.memo(
    React.forwardRef<HTMLDivElement, PopoverProps>(({ assist, onMessageChange, ...props }, ref) => {
        // index of current message in assist
        const [index, setIndex] = useState<number>(0);

        const messageContent = assist.message.content;
        const back = index === messageContent.length - 1;

        const handlePropagation = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            e.preventDefault();
        }, []);
        const handleClick = useCallback(() => {
            setIndex((prevState) => ++prevState % messageContent.length);
            onMessageChange && onMessageChange();
        }, [messageContent.length, onMessageChange]);

        return (
            <S.Popover ref={ref} {...props} $status={assist.status} onMouseDown={handlePropagation}>
                {messageContent[index].text.map((s, index) => (
                    <S.Row key={index}>
                        <Wikitext wikitext={buildLine(s, assist.messageParameters)} />
                    </S.Row>
                ))}
                {messageContent.length > 1 && (
                    <FlexRow $justifyContent={'flex-end'}>
                        <S.HintBtn $status={!back ? assist.status : undefined} onClick={handleClick}>
                            {back ? 'Back' : 'Show me more'}
                        </S.HintBtn>
                    </FlexRow>
                )}
            </S.Popover>
        );
    })
);
